import React, { MouseEvent, PureComponent } from 'react';
import styled, { css, StyledComponent } from 'styled-components';
import { Grid, Typography, Colors, Theme, Pagination, AnalyticsComponent } from '@inplayer-org/inplayer-ui';

// utils
import truncate from 'lodash/truncate';

// components
import StyledContainer from 'components/StyledContainer';
import Loader from 'components/Loader';

const { Container, Cell } = Grid;

interface ItemBoxProps {
  isSelected?: boolean;
  itemBoxWidth?: string;
  isBackgroundColor?: boolean;
}

const ItemBox = styled(Container)<ItemBoxProps>`
  width: ${({ itemBoxWidth }) => itemBoxWidth};
  cursor: pointer;
  border: 1px solid ${Colors.lightGray};
  min-height: 4rem;
  overflow: hidden;
  padding: 0.4rem;
  transition: all 200ms ease-in-out;
  background-color: ${Colors.white};

  &:hover {
    transform: scale(1.03);
    box-shadow: ${Colors.gray} 0px 0.2rem 0.5rem;
  }

  ${({ isSelected }) =>
    isSelected &&
    css`
      transform: scale(1.03);
      box-shadow: ${Colors.gray} 0px 0.2rem 0.5rem;
    `}
`;

interface ItemContainerProps {
  rows: number;
}

const ItemContainer: StyledComponent<typeof Container, Theme, ItemContainerProps> = styled(Container)`
  height: ${({ rows }: ItemContainerProps) => `${rows * 6.67}rem`};
`;

const ItemThumbnail = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  vertical-align: middle;
`;

const TypographyStyled = styled(Typography)`
  font-size: ${({ theme }) => theme.font.sizes.small};
`;

type ItemId = string;

export interface Item {
  id: ItemId;
  label: string;
  thumbnail: string;
}

interface Props {
  currentValue?: string;
  items: Array<Item>;
  totalItemsCount: number;
  numColumns: number;
  onPageSelect: (page: number, limit: number) => void;
  onItemSelect: (item: Item) => void;
  currentPage: number;
  isLoading?: boolean;
  limitPerPage?: number;
  pageRange?: number;
  showItemId?: boolean;
  headerText?: string;
  itemBoxWidth?: string;
  isBackgroundColor?: boolean;
}

interface State {
  currentSelection: ItemId;
  currentPage: number;
}

class ThumbnailItemPicker extends PureComponent<Props, State> {
  static defaultProps = {
    currentValue: '',
    limitPerPage: 15,
    pageRange: 5,
    isLoading: false,
    showItemId: true,
    headerText: '',
  };

  constructor(props: Props) {
    super(props);

    const { currentPage } = props;

    this.state = { currentSelection: '', currentPage };
  }

  componentDidUpdate() {
    const { currentPage: currPageInProps } = this.props;
    const { currentPage: currPageInState } = this.state;

    if (currPageInProps !== currPageInState) {
      this.setState({ currentPage: currPageInProps });
    }
  }

  renderItem = (item: Item) => {
    const { id, label, thumbnail } = item;

    const { showItemId, currentValue, itemBoxWidth, isBackgroundColor } = this.props;
    const { currentSelection } = this.state;

    const isSelected = id === (currentValue || currentSelection);

    return (
      <AnalyticsComponent>
        {({ pages, tracker, merchantId, ip }) => (
          <ItemBox
            itemBoxWidth={itemBoxWidth}
            key={id}
            columns={7}
            onClick={(e) => {
              tracker.track({
                event: 'click',
                type: 'box',
                tag: `box_asset_${id}`,
                pages,
                merchantId,
                ip,
              });
              return this.handleSelectItem(item)(e);
            }}
            isSelected={isSelected}
            isBackgroundColor={isBackgroundColor}
            data-testid={`item-box${id}`}
          >
            <Cell width={2} center middle>
              <ItemThumbnail alt="thumbnail-img" data-testid={thumbnail} src={thumbnail} />
            </Cell>
            <Cell width={4} left={3} middle>
              {showItemId && (
                <TypographyStyled variant="p" data-testid={id}>
                  id: {id}
                </TypographyStyled>
              )}
              <TypographyStyled variant="p">{truncate(label, { length: 15 })}</TypographyStyled>
            </Cell>
          </ItemBox>
        )}
      </AnalyticsComponent>
    );
  };

  handleSelectItem = (item: Item) => (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault();

    const { id } = item;
    const { onItemSelect } = this.props;

    this.setState({ currentSelection: id });
    onItemSelect(item);
  };

  onPageSelect = (page: number) => {
    const { onPageSelect, limitPerPage } = this.props;

    this.setState({ currentPage: page });
    onPageSelect(page, limitPerPage!);
  };

  render() {
    const { items, totalItemsCount, numColumns, limitPerPage, pageRange, isLoading, headerText } = this.props;
    const { currentPage } = this.state;

    const rowCount = Math.ceil(Math.min(totalItemsCount, limitPerPage!) / numColumns);

    const isPaginationNeeded = totalItemsCount > limitPerPage!;

    return (
      <StyledContainer margin="2rem 0 0 0" rowGap="1rem" columns={1}>
        {headerText && (
          <Cell>
            <Typography variant="p">{headerText}</Typography>
          </Cell>
        )}
        <Cell>
          <ItemContainer columns={numColumns} rows={rowCount} gap="2rem 1.5rem">
            {isLoading ? (
              <Cell width={numColumns} height={rowCount} middle center>
                <Loader position="relative" />
              </Cell>
            ) : (
              items.map(this.renderItem)
            )}
          </ItemContainer>
        </Cell>
        {isPaginationNeeded && (
          <Cell middle>
            <StyledContainer columns={1} margin="2rem 0 0 0">
              <Cell>
                <Pagination
                  numberOfPagesDisplayed={pageRange!}
                  totalItems={totalItemsCount}
                  itemsPerPage={limitPerPage}
                  startPage={currentPage}
                  onPageChange={this.onPageSelect}
                />
              </Cell>
            </StyledContainer>
          </Cell>
        )}
      </StyledContainer>
    );
  }
}

export default ThumbnailItemPicker;
